import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useChatSessions from './useChatSessions';
import { interactiveServiceFetch } from '../../services/interactiveServiceFetch';
import {
    CONTEXT_PROD_MODE,
    ERROR_TYPES,
    EVENT_TYPES,
    INTERACTIVE_SERVICE_BASE_URL,
    MESSAGE_ROLES,
    SESSION_TITLE_PLACEHOLDER_MAX_LENGTH,
    SESSION_TYPES,
    STEP_STATUS,
    SUBMIT_STATUS,
} from '../constants';

// Sentinel key used in the per-session maps for the "new chat" view (no real session id yet).
export const NEW_CHAT_KEY = '__new_chat__';

const createMessageId = () => `${MESSAGE_ROLES.ASSISTANT}-${Date.now()}`;

export const SSE_COMPLETION_EVENTS = [
    EVENT_TYPES.COMPLETED,
    EVENT_TYPES.ERROR,
    EVENT_TYPES.USER_ERROR,
    EVENT_TYPES.CANCELLED,
    EVENT_TYPES.FINISH,
    EVENT_TYPES.CONFIRMATION_REQUIRED,
];

const createAssistantMessage = (messageId, runId = null) => ({
    id: messageId,
    runId,
    role: MESSAGE_ROLES.ASSISTANT,
    content: [],
    isThinking: true,
    isStreaming: false,
    createdAt: new Date(),
    startedAt: Date.now(),
    finishedAt: null,
    steps: [],
    ephemeralUpdates: [],
    reasoning: [],
    contextFiles: [],
});

const updateMessageSteps = (existingSteps, newStep) => {
    const stepIndex = existingSteps.findIndex(s => s.step_id === newStep.step_id);
    const updatedSteps =
        stepIndex >= 0
            ? existingSteps.map((step, idx) => (idx === stepIndex ? { ...step, ...newStep } : step))
            : [...existingSteps, newStep];

    return {
        steps: updatedSteps,
        isThinking: updatedSteps.some(s => s.status === STEP_STATUS.RUNNING),
    };
};

const sessionKeyFor = sessionId => sessionId ?? NEW_CHAT_KEY;

const TOOL_EXECUTION_FAILED = 'Tool execution failed';
const READ_FILE_TOOL_NAME = 'read_file';
const MARKDOWN_FILE_EXTENSION = '.md';

const extractToolError = ({ output, isError, errorText, error } = {}) => {
    if (output?.status === 'error') return output.error || TOOL_EXECUTION_FAILED;
    if (output?.is_error || output?.isError) return output.error || output.errorText || TOOL_EXECUTION_FAILED;
    if (isError) return errorText || error || TOOL_EXECUTION_FAILED;
    return null;
};

const useChat = ({
    runPath = 'runs',
    sessionType,
    onFinish,
    loadSessionsOnMount = true,
    contextEnv = CONTEXT_PROD_MODE,
    adminMode = false,
    isViewingConversation = true,
} = {}) => {
    const api = INTERACTIVE_SERVICE_BASE_URL;

    // Per-session message arrays, keyed by sessionId (or NEW_CHAT_KEY for the pending new-chat bucket).
    const [sessionMessages, setSessionMessages] = useState({});
    // Active runs per session key. Each entry is { runId, streamingMessageId }.
    const [activeRuns, setActiveRuns] = useState({});
    // Per-session error messages.
    const [sessionErrors, setSessionErrors] = useState({});
    const [stopError, setStopError] = useState(null);
    const [isCancelling, setIsCancelling] = useState(false);
    // Per-session pending input text, keyed the same way as sessionMessages.
    const [sessionInputs, setSessionInputs] = useState({});
    // Fired by clearCurrentSession so the composer can reset state useChat does not own
    // (its staged attachments). A ref rather than state because clearing while already
    // clear must not re-render - see the no-op updates in clearCurrentSession.
    const clearListenersRef = useRef(new Set());
    const subscribeToClearCurrentSession = useCallback(listener => {
        clearListenersRef.current.add(listener);
        return () => clearListenersRef.current.delete(listener);
    }, []);

    const onFinishRef = useRef(onFinish);
    const currentKeyRef = useRef(NEW_CHAT_KEY);
    const sessionMessagesRef = useRef({});
    const sessionInputsRef = useRef({});
    const activeRunsRef = useRef({});
    const pendingSubmissionsRef = useRef(new Set());

    const setInput = useCallback(value => {
        setSessionInputs(prev => {
            const key = currentKeyRef.current;
            const current = prev[key] ?? '';
            const next = typeof value === 'function' ? value(current) : value;
            if (next === current) return prev;
            return { ...prev, [key]: next };
        });
    }, []);

    useEffect(() => {
        sessionMessagesRef.current = sessionMessages;
    }, [sessionMessages]);

    useEffect(() => {
        sessionInputsRef.current = sessionInputs;
    }, [sessionInputs]);

    useEffect(() => {
        activeRunsRef.current = activeRuns;
    }, [activeRuns]);

    useEffect(() => {
        onFinishRef.current = onFinish;
    }, [onFinish]);

    const updateSessionMessages = useCallback((key, updater) => {
        setSessionMessages(prev => {
            const current = prev[key] ?? [];
            const next = typeof updater === 'function' ? updater(current) : updater;
            return { ...prev, [key]: next };
        });
    }, []);

    const setStreamingMessageIdFor = useCallback((key, messageId) => {
        setActiveRuns(prev => {
            const existing = prev[key];
            if (!existing) return prev;
            return { ...prev, [key]: { ...existing, streamingMessageId: messageId } };
        });
    }, []);

    const removeActiveRun = useCallback(key => {
        if (key in activeRunsRef.current) {
            const { [key]: _, ...rest } = activeRunsRef.current;
            activeRunsRef.current = rest;
        }
        setActiveRuns(prev => {
            if (!(key in prev)) return prev;
            const { [key]: _, ...rest } = prev;
            return rest;
        });
    }, []);

    const purgeSessionState = useCallback(sessionId => {
        const key = sessionKeyFor(sessionId);
        const purgeMap = prev => {
            if (!(key in prev)) return prev;
            const { [key]: _, ...rest } = prev;
            return rest;
        };
        setSessionMessages(purgeMap);
        setSessionInputs(purgeMap);
        setSessionErrors(purgeMap);
        setActiveRuns(purgeMap);
    }, []);

    const attachRun = useCallback((sessionId, runId) => {
        if (!sessionId || !runId || activeRunsRef.current[sessionId]) return;
        setActiveRuns(prev => ({ ...prev, [sessionId]: { runId, streamingMessageId: null } }));
    }, []);

    const onSessionChange = useCallback(
        (sessionId, shouldClearMessages, loadedMessages = null, sessionError = null, activeRunId = null) => {
            const key = sessionKeyFor(sessionId);
            if (shouldClearMessages) {
                // Only clear if no active run is in flight for this key.
                if (!activeRunsRef.current[key]) {
                    updateSessionMessages(key, []);
                }
            } else if (loadedMessages) {
                // Merge instead of replace: fetched history is authoritative for persisted turns, while the
                // message a live stream is building stays on top (a persisted stale copy of it, if any, is dropped).
                updateSessionMessages(key, prev => {
                    const streamingMessageId = activeRunsRef.current[key]?.streamingMessageId;
                    const streamingMessage = streamingMessageId
                        ? prev.find(({ id }) => id === streamingMessageId)
                        : null;
                    if (!streamingMessage) return loadedMessages;
                    const history = loadedMessages.filter(
                        ({ role, runId }) =>
                            role !== MESSAGE_ROLES.ASSISTANT || !runId || runId !== streamingMessage.runId
                    );
                    return [...history, streamingMessage];
                });
            }
            setSessionErrors(prev => ({ ...prev, [key]: sessionError ?? null }));
            setStopError(null);
            // If the backend reports an active run for this session, attach to it so streaming resumes after refresh.
            if (activeRunId && !activeRunsRef.current[key]) {
                setActiveRuns(prev => ({ ...prev, [key]: { runId: activeRunId, streamingMessageId: null } }));
            }
        },
        [updateSessionMessages]
    );

    const {
        currentSessionId,
        currentSessionType,
        setCurrentSessionId,
        sessions,
        isLoadingSessions,
        sessionsLoadError,
        loadingSessionId,
        sessionDeletedNotice,
        sessionDeleteError,
        sessionLoadError,
        fetchSessions,
        upsertSession,
        loadSession,
        deleteSession,
        updateSessionTitle,
    } = useChatSessions({
        api,
        sessionType,
        onSessionChange,
        onSessionRemoved: purgeSessionState,
        loadSessionsOnMount,
    });

    const currentKey = sessionKeyFor(currentSessionId);
    currentKeyRef.current = currentKey;
    const messages = sessionMessages[currentKey] ?? [];
    const isLoading = !!activeRuns[currentKey];
    const activeRunId = activeRuns[currentKey]?.runId ?? null;
    const error = sessionErrors[currentKey] ?? null;
    const input = sessionInputs[currentKey] ?? '';

    // When the session list reports active runs we don't yet know about, attach to them.
    // Covers the case of returning to the page while a stream from a previous visit is still going.
    useEffect(() => {
        const additions = {};
        for (const session of sessions || []) {
            const runId = session?.active_run_id;
            const sid = session?.session_id;
            if (runId && sid && !activeRunsRef.current[sid]) {
                additions[sid] = { runId, streamingMessageId: null };
            }
        }
        if (Object.keys(additions).length === 0) return;
        setActiveRuns(prev => ({ ...prev, ...additions }));
    }, [sessions]);

    // A mention never crosses its run's stream, so a Slack conversation the listing reports as moved
    // is reloaded: the question arrives as the session's pending query, the answer streams over it.
    const listedUpdateRef = useRef({});
    useEffect(() => {
        if (!currentSessionId) return;
        const session = (sessions || []).find(entry => entry.session_id === currentSessionId);
        if (session?.session_type !== SESSION_TYPES.SLACK || session.updated_at == null) return;
        const runId = session.active_run_id ?? null;
        const lastSeen = listedUpdateRef.current[currentSessionId];
        listedUpdateRef.current[currentSessionId] = { updatedAt: session.updated_at, runId };
        // First sighting only records: opening the conversation is already loading it.
        if (lastSeen === undefined || lastSeen.updatedAt === session.updated_at) return;
        // A live run rewrites the session on every step of its own answer, which its stream is
        // already delivering. Reload on the run's arrival and once it is gone, not in between.
        if (runId && runId === lastSeen.runId) return;
        loadSession(currentSessionId);
    }, [sessions, currentSessionId, loadSession]);

    const streamingSessionIds = useMemo(() => Object.keys(activeRuns).filter(k => k !== NEW_CHAT_KEY), [activeRuns]);

    const activeViewSessionId = isViewingConversation && !loadingSessionId ? currentSessionId : null;
    const [updatedSessionIds, setUpdatedSessionIds] = useState([]);
    const previousStreamingIdsRef = useRef(streamingSessionIds);

    useEffect(() => {
        const justFinished = previousStreamingIdsRef.current.filter(id => !streamingSessionIds.includes(id));
        previousStreamingIdsRef.current = streamingSessionIds;
        const finishedWhileAway = justFinished.filter(id => id !== activeViewSessionId);
        if (finishedWhileAway.length) {
            setUpdatedSessionIds(prev => [...new Set([...prev, ...finishedWhileAway])]);
        }
    }, [streamingSessionIds, activeViewSessionId]);

    useEffect(() => {
        if (!activeViewSessionId) return;
        setUpdatedSessionIds(prev =>
            prev.includes(activeViewSessionId) ? prev.filter(id => id !== activeViewSessionId) : prev
        );
    }, [activeViewSessionId]);

    const streamDescriptors = useMemo(() => Object.entries(activeRuns).map(([key, { runId }]) => ({ key, runId })), [
        activeRuns,
    ]);

    const clearCurrentSession = useCallback(() => {
        setCurrentSessionId(null);
        clearListenersRef.current.forEach(listener => listener());
        // Only clear the idle new-chat bucket; no-op updates avoid re-triggering the pending-prompt auto-run.
        if (!activeRunsRef.current[NEW_CHAT_KEY] && !pendingSubmissionsRef.current.has(NEW_CHAT_KEY)) {
            setSessionMessages(prev =>
                (prev[NEW_CHAT_KEY] ?? []).length === 0 ? prev : { ...prev, [NEW_CHAT_KEY]: [] }
            );
        }
        setSessionErrors(prev => (prev[NEW_CHAT_KEY] == null ? prev : { ...prev, [NEW_CHAT_KEY]: null }));
        setSessionInputs(prev => {
            if (!(NEW_CHAT_KEY in prev)) return prev;
            const { [NEW_CHAT_KEY]: _, ...rest } = prev;
            return rest;
        });
    }, [setCurrentSessionId]);

    const setErrorFor = useCallback((key, value) => {
        setSessionErrors(prev => ({ ...prev, [key]: value }));
    }, []);

    const handleError = useCallback(
        (key, errorMessage, isUserError = false) => {
            const messageId = activeRunsRef.current[key]?.streamingMessageId;
            removeActiveRun(key);

            if (!isUserError) {
                setErrorFor(key, errorMessage);
                if (!messageId) return;
                updateSessionMessages(key, prev =>
                    prev.map(msg => {
                        if (msg.id !== messageId) return msg;
                        const stoppedSteps = (msg.steps || []).map(step =>
                            step.status === STEP_STATUS.RUNNING ? { ...step, status: STEP_STATUS.FAILED } : step
                        );
                        const stoppedEphemerals = (msg.ephemeralUpdates || []).map(update =>
                            update.isStreaming ? { ...update, isStreaming: false } : update
                        );
                        const stoppedReasoning = (msg.reasoning || []).map(r =>
                            r.isStreaming ? { ...r, isStreaming: false } : r
                        );
                        return {
                            ...msg,
                            isThinking: false,
                            isStreaming: false,
                            steps: stoppedSteps,
                            ephemeralUpdates: stoppedEphemerals,
                            reasoning: stoppedReasoning,
                            finishedAt: Date.now(),
                        };
                    })
                );
                return;
            }

            setErrorFor(key, null);

            const textContent = [{ type: 'error', data: errorMessage }];
            if (!messageId) {
                updateSessionMessages(key, prev => [
                    ...prev,
                    { ...createAssistantMessage(createMessageId()), isThinking: false, content: textContent },
                ]);
            } else {
                updateSessionMessages(key, prev =>
                    prev.map(msg =>
                        msg.id === messageId
                            ? {
                                  ...msg,
                                  isThinking: false,
                                  isStreaming: false,
                                  steps: [],
                                  content: [...(msg.content || []), ...textContent],
                              }
                            : msg
                    )
                );
            }
        },
        [removeActiveRun, setErrorFor, updateSessionMessages]
    );

    const handleStarted = useCallback(
        key => {
            const messageId = createMessageId();
            const runId = activeRunsRef.current[key]?.runId ?? null;
            setStreamingMessageIdFor(key, messageId);
            updateSessionMessages(key, prev => [...prev, createAssistantMessage(messageId, runId)]);
        },
        [setStreamingMessageIdFor, updateSessionMessages]
    );

    const handleCompleted = useCallback(
        key => {
            const messageId = activeRunsRef.current[key]?.streamingMessageId;
            let finishedMessage = null;
            updateSessionMessages(key, prev => {
                const updated = prev.map(msg => {
                    if (msg.id !== messageId) return msg;

                    const completedSteps = (msg.steps || []).map(step =>
                        step.status !== STEP_STATUS.FAILED ? { ...step, status: STEP_STATUS.COMPLETED } : step
                    );
                    const completedReasoning = (msg.reasoning || []).map(r =>
                        r.isStreaming ? { ...r, isStreaming: false } : r
                    );
                    return {
                        ...msg,
                        isThinking: false,
                        isLoading: false,
                        isStreaming: false,
                        steps: completedSteps,
                        reasoning: completedReasoning,
                        finishedAt: Date.now(),
                    };
                });
                finishedMessage = updated.find(m => m.id === messageId) || null;
                return updated;
            });
            if (finishedMessage) onFinishRef.current?.(finishedMessage);
            removeActiveRun(key);
        },
        [removeActiveRun, updateSessionMessages]
    );

    const handleCancelled = useCallback(
        (key, message) => {
            const messageId = activeRunsRef.current[key]?.streamingMessageId;
            removeActiveRun(key);
            if (!messageId) return;

            const displayedMessage = message?.message ?? '**Response was interrupted**';
            updateSessionMessages(key, prev =>
                prev.map(msg => {
                    if (msg.id !== messageId) return msg;
                    const stoppedSteps = (msg.steps || []).map(step =>
                        step.status === STEP_STATUS.RUNNING ? { ...step, status: STEP_STATUS.FAILED } : step
                    );
                    const stoppedEphemerals = (msg.ephemeralUpdates || []).map(update =>
                        update.isStreaming ? { ...update, isStreaming: false } : update
                    );
                    const stoppedReasoning = (msg.reasoning || []).map(r =>
                        r.isStreaming ? { ...r, isStreaming: false } : r
                    );
                    return {
                        ...msg,
                        isThinking: false,
                        isStreaming: false,
                        steps: stoppedSteps,
                        ephemeralUpdates: stoppedEphemerals,
                        reasoning: stoppedReasoning,
                        finishedAt: Date.now(),
                        content:
                            Array.isArray(msg.content) && msg.content.length
                                ? [...msg.content, { type: 'markdown', data: `\n\n${displayedMessage}` }]
                                : [{ type: 'markdown', data: displayedMessage }],
                    };
                })
            );
        },
        [removeActiveRun, updateSessionMessages]
    );

    const handleToken = useCallback(
        (key, message) => {
            const messageId = activeRunsRef.current[key]?.streamingMessageId;
            const content = message.content || message.token || '';
            if (!messageId || !content) return;

            updateSessionMessages(key, prev =>
                prev.map(msg => {
                    if (msg.id !== messageId) return msg;

                    const updatedContent = Array.isArray(msg.content) ? [...msg.content] : [];
                    const lastBlock = updatedContent[updatedContent.length - 1];
                    if (lastBlock && lastBlock.type === 'markdown') {
                        lastBlock.data += content;
                    } else {
                        updatedContent.push({ type: 'markdown', data: content });
                    }

                    return { ...msg, content: updatedContent, isThinking: false, isStreaming: true };
                })
            );
        },
        [updateSessionMessages]
    );

    const handleStepUpdate = useCallback(
        (key, message) => {
            const messageId = activeRunsRef.current[key]?.streamingMessageId;
            const { step } = message;
            if (!step || !messageId) return;

            updateSessionMessages(key, prev =>
                prev.map(msg => (msg.id === messageId ? { ...msg, ...updateMessageSteps(msg.steps || [], step) } : msg))
            );
        },
        [updateSessionMessages]
    );

    const handleArtifact = useCallback(
        (key, message) => {
            const messageId = activeRunsRef.current[key]?.streamingMessageId;
            const { artifact } = message;
            if (!artifact || !messageId) return;

            updateSessionMessages(key, prev =>
                prev.map(msg => {
                    if (msg.id !== messageId) return msg;
                    const updatedContent = Array.isArray(msg.content) ? [...msg.content] : [];
                    updatedContent.push(artifact);
                    return { ...msg, content: updatedContent };
                })
            );
        },
        [updateSessionMessages]
    );

    const upsertEphemeralUpdate = useCallback(
        (key, updateId, mutator) => {
            const messageId = activeRunsRef.current[key]?.streamingMessageId;
            if (!messageId) return;

            updateSessionMessages(key, prev =>
                prev.map(msg => {
                    if (msg.id !== messageId) return msg;

                    const existing = msg.ephemeralUpdates || [];
                    const idx = existing.findIndex(u => u.id === updateId);
                    const current = idx >= 0 ? existing[idx] : { id: updateId, text: '', isStreaming: true };
                    const next = mutator(current);
                    const updated = idx >= 0 ? existing.map((u, i) => (i === idx ? next : u)) : [...existing, next];
                    return { ...msg, ephemeralUpdates: updated, isThinking: false };
                })
            );
        },
        [updateSessionMessages]
    );

    const handleEphemeralUpdateStart = useCallback(
        (key, message) => {
            upsertEphemeralUpdate(key, message.id, current => ({
                ...current,
                title: message.title || current.title || '',
                isStreaming: true,
            }));
        },
        [upsertEphemeralUpdate]
    );

    const handleEphemeralUpdateDelta = useCallback(
        (key, message) => {
            upsertEphemeralUpdate(key, message.id, current => ({
                ...current,
                text: (current.text || '') + (message.delta || ''),
                isStreaming: true,
            }));
        },
        [upsertEphemeralUpdate]
    );

    const handleEphemeralUpdateEnd = useCallback(
        (key, message) => {
            upsertEphemeralUpdate(key, message.id, current => ({ ...current, isStreaming: false }));
        },
        [upsertEphemeralUpdate]
    );

    const upsertReasoning = useCallback(
        (key, reasoningId, mutator) => {
            const messageId = activeRunsRef.current[key]?.streamingMessageId;
            if (!messageId) return;

            updateSessionMessages(key, prev =>
                prev.map(msg => {
                    if (msg.id !== messageId) return msg;

                    const existing = msg.reasoning || [];
                    const idx = existing.findIndex(r => r.id === reasoningId);
                    const current = idx >= 0 ? existing[idx] : { id: reasoningId, text: '', isStreaming: true };
                    const next = mutator(current);
                    const updated = idx >= 0 ? existing.map((r, i) => (i === idx ? next : r)) : [...existing, next];
                    return { ...msg, reasoning: updated };
                })
            );
        },
        [updateSessionMessages]
    );

    const handleReasoningStart = useCallback(
        (key, message) => {
            upsertReasoning(key, message.id, current => ({ ...current, isStreaming: true }));
        },
        [upsertReasoning]
    );

    const handleReasoningDelta = useCallback(
        (key, message) => {
            upsertReasoning(key, message.id, current => ({
                ...current,
                text: (current.text || '') + (message.delta || ''),
                isStreaming: true,
            }));
        },
        [upsertReasoning]
    );

    const handleReasoningEnd = useCallback(
        (key, message) => {
            upsertReasoning(key, message.id, current => ({ ...current, isStreaming: false }));
        },
        [upsertReasoning]
    );

    const handleResponseComplete = useCallback(
        key => {
            const messageId = activeRunsRef.current[key]?.streamingMessageId;
            if (!messageId) return;

            updateSessionMessages(key, prev =>
                prev.map(msg => {
                    if (msg.id !== messageId) return msg;

                    const updatedContent = Array.isArray(msg.content) ? [...msg.content] : [];
                    const lastBlock = updatedContent[updatedContent.length - 1];
                    if (lastBlock && lastBlock.type === 'markdown') {
                        lastBlock.data += '\n\n';
                    }

                    return { ...msg, isStreaming: false, content: updatedContent };
                })
            );
        },
        [updateSessionMessages]
    );

    const handleFinish = useCallback(
        (key, message) => {
            const reason = message.finishReason;
            if (reason === 'cancelled') return handleCancelled(key, message);
            if (reason === 'error') {
                if (!activeRunsRef.current[key]) return;
                return handleError(key, message.errorText || 'An error occurred', true);
            }
            return handleCompleted(key);
        },
        [handleCompleted, handleCancelled, handleError]
    );

    const handleToolInputStart = useCallback(
        (key, message) => {
            const step = {
                step_id: message.toolCallId,
                name: message.toolName,
                title: message.toolLabel || message.toolName,
                status: STEP_STATUS.RUNNING,
            };
            handleStepUpdate(key, { step });
        },
        [handleStepUpdate]
    );

    const handleToolOutputAvailable = useCallback(
        (key, message) => {
            const errorReason = extractToolError(message);
            const step = errorReason
                ? {
                      step_id: message.toolCallId,
                      status: STEP_STATUS.FAILED,
                      errorReason,
                  }
                : {
                      step_id: message.toolCallId,
                      status: STEP_STATUS.COMPLETED,
                  };
            handleStepUpdate(key, { step });
        },
        [handleStepUpdate]
    );

    const handleToolInputAvailable = useCallback(
        (key, message) => {
            if (message.toolName !== READ_FILE_TOOL_NAME) return;
            const path = message.input?.path;
            if (typeof path !== 'string' || !path.endsWith(MARKDOWN_FILE_EXTENSION)) return;

            const messageId = activeRunsRef.current[key]?.streamingMessageId;
            if (!messageId) return;

            updateSessionMessages(key, prev =>
                prev.map(msg => {
                    if (msg.id !== messageId) return msg;
                    const existing = msg.contextFiles || [];
                    if (existing.includes(path)) return msg;
                    return { ...msg, contextFiles: [...existing, path] };
                })
            );
        },
        [updateSessionMessages]
    );

    const handleDataEvent = useCallback(
        (key, message) => {
            const dataType = message.type.slice(EVENT_TYPES.DATA_PREFIX.length);
            if (dataType === 'artifact' && message.data) {
                const artifactData = message.data;
                const artifact = {
                    type: artifactData.artifact_type || dataType,
                    data: artifactData.data,
                    content_url: artifactData.content_url,
                    title: artifactData.title,
                    insights: artifactData.insights,
                    metadata: artifactData.metadata,
                };
                handleArtifact(key, { artifact });
            }
        },
        [handleArtifact]
    );

    const handleStreamMessage = useCallback(
        (key, message) => {
            const { type } = message;

            // Lifecycle events always dispatch.
            if (type === EVENT_TYPES.STARTED || type === EVENT_TYPES.START) return handleStarted(key);
            if (type === EVENT_TYPES.COMPLETED) return handleCompleted(key);
            if (type === EVENT_TYPES.CANCELLED) return handleCancelled(key, message);
            if (type === EVENT_TYPES.RESPONSE_COMPLETE) return handleResponseComplete(key);
            if (type === EVENT_TYPES.SESSION_TITLE_UPDATED) return upsertSession(message.session_id, message.title);
            if (type === EVENT_TYPES.FINISH) return handleFinish(key, message);
            if (type === EVENT_TYPES.STREAM_ERROR)
                return handleError(key, message.errorText || 'An error occurred', true);

            if (Object.values(ERROR_TYPES).includes(type)) {
                return handleError(
                    key,
                    message.message || message.error || 'An error occurred',
                    type === ERROR_TYPES.USER_ERROR
                );
            }

            // Content events only dispatch while a streaming message exists.
            if (!activeRunsRef.current[key]?.streamingMessageId) return;

            if (type === EVENT_TYPES.TOKEN || type === EVENT_TYPES.TOKEN_BATCH) return handleToken(key, message);
            if (type === EVENT_TYPES.TEXT_DELTA) return handleToken(key, { content: message.delta });
            if (type === EVENT_TYPES.TEXT_END) return handleResponseComplete(key);
            if (type === EVENT_TYPES.REASONING_START) return handleReasoningStart(key, message);
            if (type === EVENT_TYPES.REASONING_DELTA) return handleReasoningDelta(key, message);
            if (type === EVENT_TYPES.REASONING_END) return handleReasoningEnd(key, message);
            if (type === EVENT_TYPES.STEP_UPDATE) return handleStepUpdate(key, message);
            if (type === EVENT_TYPES.ARTIFACT) return handleArtifact(key, message);
            if (type === EVENT_TYPES.TOOL_INPUT_START) return handleToolInputStart(key, message);
            if (type === EVENT_TYPES.TOOL_INPUT_AVAILABLE) return handleToolInputAvailable(key, message);
            if (type === EVENT_TYPES.TOOL_OUTPUT_AVAILABLE) return handleToolOutputAvailable(key, message);
            if (type === EVENT_TYPES.EPHEMERAL_UPDATE_START) return handleEphemeralUpdateStart(key, message);
            if (type === EVENT_TYPES.EPHEMERAL_UPDATE_DELTA) return handleEphemeralUpdateDelta(key, message);
            if (type === EVENT_TYPES.EPHEMERAL_UPDATE_END) return handleEphemeralUpdateEnd(key, message);
            if (type?.startsWith(EVENT_TYPES.DATA_PREFIX)) return handleDataEvent(key, message);
        },
        [
            handleStarted,
            handleCompleted,
            handleCancelled,
            handleFinish,
            handleError,
            handleResponseComplete,
            handleToken,
            handleStepUpdate,
            handleArtifact,
            handleToolInputStart,
            handleToolInputAvailable,
            handleToolOutputAvailable,
            handleEphemeralUpdateStart,
            handleEphemeralUpdateDelta,
            handleEphemeralUpdateEnd,
            handleReasoningStart,
            handleReasoningDelta,
            handleReasoningEnd,
            handleDataEvent,
            upsertSession,
        ]
    );

    const handleStreamError = useCallback(
        (key, errorMessage) => {
            handleError(key, errorMessage);
        },
        [handleError]
    );

    const handleSubmit = useCallback(
        async (e, options = {}) => {
            e?.preventDefault();

            const submissionKey = sessionKeyFor(currentSessionId);
            const rawContent = options?.content || sessionInputsRef.current[submissionKey] || '';
            const messageContent = rawContent.trim();
            if (!messageContent) return SUBMIT_STATUS.REJECTED;

            // Block a second submission to the same view while it has an active run,
            // including the window where the run is still being created.
            if (activeRunsRef.current[submissionKey] || pendingSubmissionsRef.current.has(submissionKey)) {
                return SUBMIT_STATUS.REJECTED;
            }
            pendingSubmissionsRef.current.add(submissionKey);

            setStopError(null);
            setErrorFor(submissionKey, null);

            const attachmentBlocks = options?.attachmentBlocks || [];
            const userMessage = {
                id: `${MESSAGE_ROLES.USER}-${Date.now()}`,
                role: MESSAGE_ROLES.USER,
                content: [...attachmentBlocks, { type: 'markdown', data: messageContent }],
                createdAt: new Date(),
            };

            updateSessionMessages(submissionKey, prev => [...prev, userMessage]);
            setSessionInputs(prev => {
                if (!(submissionKey in prev)) return prev;
                const { [submissionKey]: _, ...rest } = prev;
                return rest;
            });

            const endpoint = `${api}/${runPath}`;
            const requestBody = options?.body || {
                user_query: messageContent,
                request_id: `req-${Date.now()}`,
                session_id: currentSessionId,
                context_env: contextEnv,
                session_type: sessionType,
                admin_mode: adminMode,
                ...(options?.attachments?.length ? { attachments: options.attachments } : {}),
            };

            try {
                const response = await interactiveServiceFetch(endpoint, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(requestBody),
                });

                if (!response.ok) {
                    // The server's detail is the only thing that explains an
                    // attachment rejection ("Attachment already used", the session
                    // file limit); the bare status says nothing useful.
                    // A 422 detail is an array of field errors, so only take a string.
                    const detail = await response
                        .json()
                        .then(body => (typeof body?.detail === 'string' ? body.detail : null))
                        .catch(() => null);
                    throw new Error(detail || `Failed to create run: ${response.status} ${response.statusText}`);
                }

                const result = await response.json();
                const runId = result.run_id;
                const newSessionId = result.session_id || currentSessionId;
                const finalKey = sessionKeyFor(newSessionId);

                // If we submitted from the new-chat bucket and got back a real session id, migrate state.
                if (submissionKey === NEW_CHAT_KEY && finalKey !== NEW_CHAT_KEY) {
                    setSessionMessages(prev => {
                        if (!(NEW_CHAT_KEY in prev)) return prev;
                        const { [NEW_CHAT_KEY]: pendingMessages, ...rest } = prev;
                        return { ...rest, [finalKey]: pendingMessages };
                    });
                    setSessionErrors(prev => {
                        const { [NEW_CHAT_KEY]: _, ...rest } = prev;
                        return { ...rest, [finalKey]: null };
                    });
                    // Carry over any text typed in the new-chat input between submit and response.
                    setSessionInputs(prev => {
                        if (!(NEW_CHAT_KEY in prev)) return prev;
                        const { [NEW_CHAT_KEY]: pendingInput, ...rest } = prev;
                        return pendingInput ? { ...rest, [finalKey]: pendingInput } : rest;
                    });
                }

                setActiveRuns(prev => ({
                    ...prev,
                    [finalKey]: { runId, streamingMessageId: null },
                }));

                setCurrentSessionId(newSessionId);

                if (newSessionId && newSessionId !== currentSessionId) {
                    const placeholderTitle =
                        messageContent.length > SESSION_TITLE_PLACEHOLDER_MAX_LENGTH
                            ? `${messageContent.slice(0, SESSION_TITLE_PLACEHOLDER_MAX_LENGTH)}...`
                            : messageContent;
                    upsertSession(newSessionId, placeholderTitle);
                }
            } catch (err) {
                handleError(submissionKey, err.message || 'An error occurred');
                return SUBMIT_STATUS.FAILED;
            } finally {
                pendingSubmissionsRef.current.delete(submissionKey);
            }
            return SUBMIT_STATUS.CREATED;
        },
        [
            api,
            runPath,
            sessionType,
            currentSessionId,
            setCurrentSessionId,
            upsertSession,
            handleError,
            contextEnv,
            adminMode,
            updateSessionMessages,
            setErrorFor,
        ]
    );

    const append = useCallback(
        async message => {
            await handleSubmit(null, { content: message.content });
        },
        [handleSubmit]
    );

    const reload = useCallback(
        async (submit = handleSubmit) => {
            const currentMessages = sessionMessagesRef.current[currentKey] ?? [];
            const lastUserMessage = [...currentMessages].reverse().find(m => m.role === MESSAGE_ROLES.USER);
            if (!lastUserMessage) return;

            const userMessageIndex = currentMessages.findIndex(m => m.id === lastUserMessage.id);
            updateSessionMessages(currentKey, currentMessages.slice(0, userMessageIndex + 1));

            // Text only from the message itself: an upload a previous run consumed is gone.
            await submit(null, { content: lastUserMessage.content?.find(b => b.type === 'markdown')?.data });
        },
        [currentKey, handleSubmit, updateSessionMessages]
    );

    const stop = useCallback(
        async (sessionId = currentSessionId) => {
            const key = sessionKeyFor(sessionId);
            const activeRun = activeRunsRef.current[key];
            if (!activeRun) return;
            const isCurrentSession = key === currentKey;
            if (isCurrentSession) {
                setStopError(null);
                setIsCancelling(true);
            }
            try {
                const response = await interactiveServiceFetch(`${api}/runs/${activeRun.runId}/cancel`, {
                    method: 'POST',
                });
                if (!response.ok && isCurrentSession) setStopError('Failed to interrupt query');
            } catch (err) {
                console.error('Failed to cancel run:', err);
                if (isCurrentSession) setStopError('Failed to interrupt query');
            } finally {
                if (isCurrentSession) setIsCancelling(false);
            }
        },
        [api, currentKey, currentSessionId]
    );

    const handleInputChange = useCallback(e => setInput(e.target.value), []);

    const deleteSessionAndCancelRun = useCallback(
        async sessionId => {
            await stop(sessionId);
            return deleteSession(sessionId);
        },
        [stop, deleteSession]
    );

    return {
        sessionType,
        messages,
        input,
        setInput,
        handleSubmit,
        handleInputChange,
        isLoading,
        isCancelling,
        activeRunId,
        error,
        stopError,
        append,
        reload,
        stop,
        // Session management
        currentSessionId,
        currentSessionType,
        subscribeToClearCurrentSession,
        sessions,
        isLoadingSessions,
        sessionsLoadError,
        loadingSessionId,
        sessionDeletedNotice,
        sessionDeleteError,
        sessionLoadError,
        clearCurrentSession,
        loadSession,
        deleteSession: deleteSessionAndCancelRun,
        updateSessionTitle,
        fetchSessions,
        // Multi-stream
        streamDescriptors,
        streamingSessionIds,
        updatedSessionIds,
        handleStreamMessage,
        handleStreamError,
        attachRun,
        purgeSessionState,
    };
};

export default useChat;
